/*
 * Chip glyphs for the selection-aware action dock.
 *
 * THESE ARE TEMPORARY PLACEHOLDERS, on the same footing as
 * components/deckorateHome/deckIcons.tsx — the UI/UX team will supply the
 * final SVGs. Replacing one is a single-line change: swap only the JSX handed
 * to `chipIcon(...)`.
 *
 * Rules for a replacement:
 *   • Do NOT fork the shared wrapper — every glyph renders through the same
 *     <svg> (one viewBox, one stroke weight, `currentColor`) so the row stays
 *     visually even and the chip's hover/active colour tints the icon.
 *   • Size is set in CSS (`.chipIcon svg` in ActionChipDock.module.css), so a
 *     replacement inherits the right dimensions whatever it declares itself.
 *   • Keep the export name — chipConfig.ts wires glyphs to chips by name.
 *
 * Icons already drawn for the Deckorate ribbon cards are re-exported from
 * deckIcons rather than redrawn here, so the two surfaces can't drift.
 */

import type { ComponentType, ReactNode } from "react";

/* Shared wrapper — the single source of chip-glyph sizing/stroke/colour. */
const chipIcon =
  (inner: ReactNode): ComponentType =>
  function ChipIconPlaceholder() {
    return (
      <svg
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        data-placeholder-icon="true"
      >
        {inner}
      </svg>
    );
  };

/* ── Reused ribbon glyphs (same action, same picture) ── */

export {
  /** Create slides — sparkle. */
  CreateSlidesIcon,
  /** Fix alignment — ragged-then-flush lines. */
  FixAlignIcon,
  /** Transform slide — the AI/palette brain. */
  ColorToolsIcon as TransformSlideIcon,
  /** Speaker notes — notes card with a tail. */
  SpeakerNotesIcon,
} from "../../components/deckorateHome/deckIcons";

/* ── Chip-only glyphs ── */

/** Pencil — every "rewrite this text" chip. */
export const PencilIcon = chipIcon(
  <>
    <path d="M13 4l3 3-9 9H4v-3z" />
    <path d="M12 5l3 3" />
  </>,
);

/** Lightbulb — Get Insights. */
export const InsightsIcon = chipIcon(
  <>
    <path d="M7 12a4 4 0 116 0c-.6.7-1 1.3-1 2H8c0-.7-.4-1.3-1-2z" />
    <path d="M8.5 17h3" />
    <path d="M10 2v1M3.7 5.2l.7.7M16.3 5.2l-.7.7" />
  </>,
);

/** Magnifier — Improvements / Find anomalies. */
export const SearchIcon = chipIcon(
  <>
    <circle cx="9" cy="9" r="5" />
    <path d="M17 17l-4.5-4.5" />
  </>,
);

/** Bar columns — Infographic. */
export const InfographicIcon = chipIcon(
  <>
    <path d="M3 17h14" />
    <path d="M5 17V9M9 17V5M13 17v-6M16 17V7" />
  </>,
);

/** Scissors — Make concise. */
export const ConciseIcon = chipIcon(
  <>
    <circle cx="5" cy="6" r="2" />
    <circle cx="5" cy="14" r="2" />
    <path d="M6.5 7.2L16 14M6.5 12.8L16 6" />
  </>,
);

/** Sparkle on a frame — Add icon. */
export const AddIconIcon = chipIcon(
  <>
    <rect x="3" y="5" width="9" height="9" rx="2" />
    <path d="M15.5 3l.7 1.9 1.9.7-1.9.7-.7 1.9-.7-1.9-1.9-.7 1.9-.7z" />
  </>,
);

/** Open book — Expand text. */
export const ExpandTextIcon = chipIcon(
  <>
    <path d="M10 5C8 3.5 5 3.5 3 4v11c2-.5 5-.5 7 1 2-1.5 5-1.5 7-1V4c-2-.5-5-.5-7 1z" />
    <path d="M10 5v12" />
  </>,
);

/** Speech bubble with waves — Change tone. */
export const ToneIcon = chipIcon(
  <>
    <path d="M4 4h9a1 1 0 011 1v6a1 1 0 01-1 1H8l-3 3V5a1 1 0 011-1z" />
    <path d="M16 7c1 1 1 4 0 5" />
  </>,
);

/** Rising line with an arrow — Convert to chart. */
export const ToChartIcon = chipIcon(
  <>
    <path d="M3 14l4-4 3 3 6-7" />
    <path d="M13 6h4v4" />
  </>,
);

/** Gridded frame — Convert to table. */
export const ToTableIcon = chipIcon(
  <>
    <rect x="4" y="3" width="12" height="14" rx="1.5" />
    <path d="M4 8h12" />
    <path d="M7 12h.01M10 12h.01M13 12h.01" />
  </>,
);

/** Clipboard — Summarize table. */
export const SummarizeIcon = chipIcon(
  <>
    <rect x="5" y="4" width="10" height="13" rx="1.5" />
    <path d="M8 4V3h4v1" />
    <path d="M7 9h6M7 12h4" />
  </>,
);

/** Head and shoulders — Photo head. */
export const PhotoHeadIcon = chipIcon(
  <>
    <circle cx="10" cy="7" r="3" />
    <path d="M4 17c0-3 3-5 6-5s6 2 6 5" />
  </>,
);

/** Nib — Vectorize / Remove background. */
export const VectorizeIcon = chipIcon(
  <>
    <path d="M4 16L14 6a3 3 0 00-4-4L3 9v4h1z" />
    <path d="M6 13l3 3" />
  </>,
);

/** Callout box — Add caption. */
export const CaptionIcon = chipIcon(
  <path d="M4 4h12a1 1 0 011 1v7a1 1 0 01-1 1H8l-4 4V5a1 1 0 011-1z" />,
);
